import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CLIENT_APPS,
  appPriority,
  isAppAvailable,
  isAppVisibleToUsers,
  readClientAppOverrides,
  readInstalledApps,
  writeInstalledApps,
  type ClientApp,
  type ClientAppOverrides,
} from '@/lib/clientApps';
import type { ZifekUser } from '@/hooks/useAuth';

interface Props {
  user: ZifekUser;
  mode?: 'store' | 'installed';
}

export default function ClientAppStoreTab({ user, mode = 'store' }: Props) {
  const [installed, setInstalled] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<ClientAppOverrides>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ClientApp | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'Toutes' | ClientApp['category']>('Toutes');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [slugs, catalog] = await Promise.all([
        readInstalledApps(user.id),
        readClientAppOverrides(),
      ]);
      setInstalled(slugs);
      setOverrides(catalog);
    } catch {
      setError('Impossible de charger vos applications.');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    load();
  }, [load]);

  const availableApps = useMemo(
    () => CLIENT_APPS.filter((app) => isAppAvailable(app, overrides) && isAppVisibleToUsers(app, overrides)),
    [overrides],
  );

  const visibleApps = useMemo(() => {
    let list = mode === 'installed'
      ? availableApps.filter((app) => installed.includes(app.slug))
      : availableApps;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (app) =>
          app.name.toLowerCase().includes(q) ||
          app.tagline.toLowerCase().includes(q) ||
          app.description.toLowerCase().includes(q) ||
          app.features.some((f) => f.toLowerCase().includes(q)),
      );
    }
    if (category !== 'Toutes') list = list.filter((app) => app.category === category);
    return [...list].sort((a, b) => appPriority(a, overrides).localeCompare(appPriority(b, overrides)));
  }, [availableApps, installed, mode, search, category, overrides]);

  const install = async (app: ClientApp) => {
    setBusy(app.slug);
    setError(null);
    try {
      const next = Array.from(new Set([...installed, app.slug]));
      await writeInstalledApps(user.id, next);
      setInstalled(next);
    } catch {
      setError("L'installation a échoué. Réessayez.");
    } finally {
      setBusy(null);
    }
  };

  const uninstall = async (app: ClientApp) => {
    setBusy(app.slug);
    setError(null);
    try {
      const next = installed.filter((slug) => slug !== app.slug);
      await writeInstalledApps(user.id, next);
      setInstalled(next);
      setSelected(null);
    } catch {
      setError('La désinstallation a échoué.');
    } finally {
      setBusy(null);
    }
  };

  const categories: Array<'Toutes' | ClientApp['category']> = ['Toutes', 'Études', 'Organisation', 'Documents', 'IA'];

  return (
    <div>
      {/* En-tête */}
      <div className="rounded-xl bg-background-100/70 border border-background-200/50 p-5 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-[11px] font-semibold">
                <i className="ri-user-smile-line"></i>
                Espace personnel
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-50 text-foreground-600 text-[11px] font-medium border border-background-200/60">
                <i className="ri-cloud-off-line"></i>
                Fonctionne hors ligne
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              {mode === 'installed' ? 'Mes applications' : 'App Store Zifek'}
            </h2>
            <p className="text-sm text-foreground-600 mt-1 max-w-2xl">
              {mode === 'installed'
                ? 'Vos outils personnels installés. Vos données restent sur cet appareil et se synchronisent au retour du réseau.'
                : 'Des mini-applications pour organiser vos cours, vos documents et vos révisions. Installez seulement ce dont vous avez besoin.'}
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-center">
              <p className="text-lg font-bold text-foreground-900 leading-none">{installed.length}</p>
              <p className="text-[11px] text-foreground-500 mt-1 whitespace-nowrap">installée(s)</p>
            </div>
            <div className="px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-center">
              <p className="text-lg font-bold text-foreground-900 leading-none">{availableApps.length}</p>
              <p className="text-[11px] text-foreground-500 mt-1 whitespace-nowrap">disponibles</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="cursor-pointer" title="Fermer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Filtres */}
      {mode === 'store' && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une application..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  category === cat
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 border border-background-200/60 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grille */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : visibleApps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/40 rounded-xl text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-apps-2-line text-xl text-foreground-500"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm">
            {mode === 'installed' ? 'Aucune application installée' : 'Aucun résultat'}
          </h3>
          <p className="text-foreground-400 text-xs mt-1 max-w-sm">
            {mode === 'installed'
              ? "Rendez-vous dans l'App Store pour installer vos premiers outils."
              : 'Essayez un autre mot-clé ou une autre catégorie.'}
          </p>
          {mode === 'installed' && (
            <Link
              to="/mon-compte/appstore"
              className="mt-4 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap no-underline"
            >
              Ouvrir l'App Store
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleApps.map((app) => {
            const isInstalled = installed.includes(app.slug);
            return (
              <article
                key={app.slug}
                className="bg-background-50 border border-background-200/50 rounded-lg overflow-hidden flex flex-col hover:border-background-300/70 transition-colors"
              >
                <button
                  onClick={() => setSelected(app)}
                  className="relative w-full h-36 bg-background-100 overflow-hidden cursor-pointer group"
                  title={`Voir ${app.name}`}
                >
                  <img
                    src={app.image}
                    alt={app.name}
                    title={`${app.name} — mini-application Zifek`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded-full bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold text-foreground-700">
                      {app.category}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-foreground-950/80 text-background-50 text-[10px] font-bold">
                      {appPriority(app, overrides)}
                    </span>
                  </div>
                  {app.offline && (
                    <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center text-foreground-700">
                      <i className="ri-cloud-off-line text-xs" title="Disponible hors ligne"></i>
                    </span>
                  )}
                </button>

                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start gap-2 mb-1.5">
                    <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                      <i className={`${app.icon} text-sm`}></i>
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-900 truncate">{app.name}</h3>
                      <p className="text-[11px] text-foreground-500 truncate">{app.tagline}</p>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3 mb-3 flex-1">{app.description}</p>

                  <div className="flex items-center gap-2">
                    {isInstalled ? (
                      <>
                        <Link
                          to={app.route}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap no-underline hover:bg-primary-600 transition-colors"
                        >
                          <i className="ri-external-link-line"></i>
                          Ouvrir
                        </Link>
                        <button
                          onClick={() => uninstall(app)}
                          disabled={busy === app.slug}
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                          title="Désinstaller"
                        >
                          {busy === app.slug ? (
                            <i className="ri-loader-4-line animate-spin text-sm"></i>
                          ) : (
                            <i className="ri-delete-bin-line text-sm"></i>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setSelected(app)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
                        >
                          <i className="ri-information-line"></i>
                          Détails
                        </button>
                        <button
                          onClick={() => install(app)}
                          disabled={busy === app.slug}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          {busy === app.slug ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            <i className="ri-download-2-line"></i>
                          )}
                          Installer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Fiche détaillée */}
      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/45" onClick={() => setSelected(null)} aria-hidden="true" />
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl">
            <div className="relative h-40 bg-background-100">
              <img
                src={selected.image}
                alt={selected.name}
                title={`${selected.name} — mini-application Zifek`}
                className="w-full h-full object-cover object-top"
              />
              <button
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background-50/90 flex items-center justify-center text-foreground-700 hover:text-foreground-950 cursor-pointer"
                title="Fermer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold">
                  {selected.category}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-bold">
                  Priorité {appPriority(selected, overrides)}
                </span>
                {selected.offline && (
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                    Hors ligne
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold font-heading text-foreground-950">{selected.name}</h3>
              <p className="text-sm text-foreground-600 mt-1.5 leading-relaxed">{selected.description}</p>

              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mt-5 mb-2">
                Ce que vous pouvez faire
              </h4>
              <ul className="space-y-1.5">
                {selected.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2 mt-6">
                {installed.includes(selected.slug) ? (
                  <>
                    <Link
                      to={selected.route}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap no-underline hover:bg-primary-600"
                    >
                      <i className="ri-external-link-line"></i>
                      Ouvrir l'application
                    </Link>
                    <button
                      onClick={() => uninstall(selected)}
                      className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 cursor-pointer"
                    >
                      Désinstaller
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => install(selected)}
                    disabled={busy === selected.slug}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-foreground-800 cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-download-2-line"></i>
                    Installer gratuitement
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}